import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiErr, LOGO } from "../lib/api";
import { LayoutDashboard, Users, Package, Settings, LogOut, X } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Negotiation", "Won", "Lost", "Follow-up Required"];
const PRODUCT_STATUSES = ["active", "coming_soon", "hidden", "draft"];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!localStorage.getItem("cultiva_token")) { setUser(false); setChecking(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("cultiva_token"); setUser(false); }).finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="min-h-screen grid place-items-center bg-ivory text-muted-foreground">Loading…</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-ivory flex">
      <aside className="w-60 bg-forest text-ivory p-6 flex flex-col fixed h-full">
        <div className="flex items-center gap-2 mb-10"><span className="bg-ivory rounded-md p-1"><img src={LOGO} alt="" className="h-9 w-auto object-contain" /></span><span className="font-serif text-xl">Cultiva Admin</span></div>
        <nav className="space-y-1 flex-1">
          {[["dashboard", "Dashboard", LayoutDashboard], ["leads", "Leads", Users], ["products", "Products", Package], ["settings", "Settings", Settings]].map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${tab === k ? "bg-ivory/10 text-ivory" : "text-ivory/60 hover:text-ivory"}`}>
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </nav>
        <button onClick={() => { api.post("/auth/logout"); localStorage.removeItem("cultiva_token"); setUser(false); }} className="flex items-center gap-3 px-4 py-2.5 text-sm text-ivory/60 hover:text-ivory" data-testid="admin-logout"><LogOut className="h-4 w-4" />Logout</button>
      </aside>
      <main className="flex-1 ml-60 p-10">
        {tab === "dashboard" && <Dashboard />}
        {tab === "leads" && <Leads />}
        {tab === "products" && <Products />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

function Login({ onLogin }) {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { const { data } = await api.post("/auth/login", { email, password }); localStorage.setItem("cultiva_token", data.token); const me = await api.get("/auth/me"); onLogin(me.data); }
    catch (e2) { setErr(apiErr(e2.response?.data?.detail)); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-forest text-ivory px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <span className="bg-ivory rounded-lg p-2 inline-block mx-auto"><img src={LOGO} alt="" className="h-16 w-auto object-contain mx-auto" /></span>
        <h1 className="font-serif text-4xl text-center mt-6">Admin Access</h1>
        <div className="space-y-5 mt-10">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-ivory/30 focus:border-ivory outline-none py-2.5" data-testid="admin-email" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border-b border-ivory/30 focus:border-ivory outline-none py-2.5" data-testid="admin-password" />
          {err && <p className="text-sm text-red-300" data-testid="admin-error">{err}</p>}
          <button type="submit" className="w-full bg-ivory text-forest rounded-full py-3 font-medium hover:bg-white transition-colors" data-testid="admin-submit">Sign In</button>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="border border-border rounded-md bg-white p-6"><div className="overline">{label}</div><div className="font-serif text-5xl text-botanical mt-2">{value}</div></div>;
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/leads/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const Bars = ({ title, data }) => (
    <div className="border border-border rounded-md bg-white p-6">
      <div className="overline mb-4">{title}</div>
      {Object.keys(data).length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> :
        Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between py-1.5 text-sm"><span className="text-botanical/80">{k || "—"}</span><span className="font-medium text-botanical">{v}</span></div>
        ))}
    </div>
  );
  return (
    <div>
      <h1 className="font-serif text-4xl text-botanical mb-8">Lead Generation Overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Leads" value={s.total} /><Stat label="New Leads" value={s.new} /><Stat label="Qualified" value={s.qualified} /><Stat label="Won" value={s.won} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Bars title="By Country" data={s.by_country} /><Bars title="By Business Type" data={s.by_business_type} /><Bars title="By Product" data={s.by_product} /><Bars title="By Enquiry Type" data={s.by_enquiry_type} />
      </div>
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { await api.patch(`/leads/${id}`, patch); load(); if (sel?.id === id) setSel({ ...sel, ...patch }); toast.success("Updated"); };
  return (
    <div>
      <h1 className="font-serif text-4xl text-botanical mb-8">Leads & Enquiries</h1>
      <div className="border border-border rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left"><tr>{["Name", "Company", "Country", "Type", "Status", "Date"].map((h) => <th key={h} className="px-4 py-3 font-medium text-botanical/70">{h}</th>)}</tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => setSel(l)} className="border-t border-border hover:bg-muted/40 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 text-botanical">{l.full_name}</td><td className="px-4 py-3 text-botanical/70">{l.company || "—"}</td>
                <td className="px-4 py-3 text-botanical/70">{l.country || l.target_country || "—"}</td><td className="px-4 py-3 text-botanical/70">{l.enquiry_type}</td>
                <td className="px-4 py-3"><span className="text-xs bg-muted px-2 py-1 rounded-full">{l.status}</span></td>
                <td className="px-4 py-3 text-botanical/50 text-xs">{(l.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setSel(null)}>
          <div className="bg-ivory w-full max-w-lg h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="lead-detail">
            <div className="flex justify-between items-start"><h2 className="font-serif text-3xl text-botanical">{sel.full_name}</h2><button onClick={() => setSel(null)}><X /></button></div>
            <p className="text-muted-foreground">{sel.company}</p>
            <div className="mt-6 space-y-3">
              <select value={sel.status} onChange={(e) => update(sel.id, { status: e.target.value })} className="w-full border border-border rounded-md p-2.5 bg-white" data-testid="lead-status-select">
                {STATUSES.map((st) => <option key={st}>{st}</option>)}
              </select>
              <textarea placeholder="Internal notes…" defaultValue={sel.notes} onBlur={(e) => update(sel.id, { notes: e.target.value })} rows={3} className="w-full border border-border rounded-md p-2.5 bg-white" />
            </div>
            <dl className="mt-6 space-y-2 text-sm">
              {[["Email", sel.email], ["Phone", sel.phone], ["City", sel.city], ["Website", sel.website], ["Business Type", sel.business_type], ["Target Country", sel.target_country], ["Estimated Volume", sel.estimated_volume], ["Products", (sel.products || []).join(", ")], ["Partnership", (sel.partnership_types || []).join(", ")], ["Pack Size", sel.pack_size], ["Quantity", sel.quantity], ["Destination", sel.destination], ["Certifications", sel.certifications], ["Message", sel.message]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex gap-3 border-b border-border pb-2"><dt className="text-muted-foreground w-36 shrink-0">{k}</dt><dd className="text-botanical">{v}</dd></div>
              ))}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/products?include_all=true").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = { name: "", slug: "", positioning: "", tagline: "", short_description: "", description: "", ingredient_story: "", color: "#1A3622", image: "", category: "", ingredients: "", nutrition: "", pack_sizes: [], verified_claims: [], usage: [], faqs: [], status: "active", b2b_available: true, featured: false };
  const save = async () => {
    try {
      const body = { ...edit, pack_sizes: typeof edit.pack_sizes === "string" ? edit.pack_sizes.split(",").map((s) => s.trim()).filter(Boolean) : edit.pack_sizes,
        verified_claims: typeof edit.verified_claims === "string" ? edit.verified_claims.split(",").map((s) => s.trim()).filter(Boolean) : edit.verified_claims,
        usage: typeof edit.usage === "string" ? edit.usage.split("\n").map((s) => s.trim()).filter(Boolean) : edit.usage };
      if (edit.id) await api.put(`/products/${edit.id}`, body); else await api.post("/products", body);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/products/${id}`); load(); toast.success("Deleted"); };
  const F = ({ k, label, ta }) => (
    <label className="block"><span className="text-xs text-muted-foreground">{label}</span>
      {ta ? <textarea rows={3} className="w-full border border-border rounded-md p-2 mt-1 bg-white text-sm" value={Array.isArray(edit[k]) ? edit[k].join(k === "usage" ? "\n" : ", ") : (edit[k] ?? "")} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />
        : <input className="w-full border border-border rounded-md p-2 mt-1 bg-white text-sm" value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} />}
    </label>
  );
  return (
    <div>
      <div className="flex justify-between items-center mb-8"><h1 className="font-serif text-4xl text-botanical">Products</h1><button onClick={() => setEdit(blank)} className="bg-botanical text-ivory rounded-full px-6 py-2.5 text-sm" data-testid="add-product-btn">Add Product</button></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} className="border border-border rounded-md bg-white p-4" data-testid={`admin-product-${p.slug}`}>
            <div className="flex gap-3"><img src={p.image} alt={p.name} className="h-16 w-16 object-contain rounded" style={{ background: `${p.color}0d` }} />
              <div className="flex-1"><h3 className="font-serif text-xl text-botanical">{p.name}</h3><span className="text-xs bg-muted px-2 py-0.5 rounded-full">{p.status}</span></div></div>
            <div className="flex gap-2 mt-4"><button onClick={() => setEdit({ ...p, pack_sizes: (p.pack_sizes || []).join(", "), verified_claims: (p.verified_claims || []).join(", "), usage: (p.usage || []).join("\n") })} className="text-sm text-botanical link-underline" data-testid={`edit-${p.slug}`}>Edit</button><button onClick={() => del(p.id)} className="text-sm text-hibiscus link-underline">Delete</button></div>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setEdit(null)}>
          <div className="bg-ivory w-full max-w-xl h-full overflow-y-auto p-8 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="product-editor">
            <div className="flex justify-between"><h2 className="font-serif text-2xl text-botanical">{edit.id ? "Edit" : "New"} Product</h2><button onClick={() => setEdit(null)}><X /></button></div>
            <div className="grid grid-cols-2 gap-3"><F k="name" label="Name" /><F k="slug" label="Slug" /><F k="positioning" label="Positioning" /><F k="tagline" label="Tagline" /><F k="category" label="Category" />
              <label className="block"><span className="text-xs text-muted-foreground">Colour</span><input type="color" className="w-full h-10 border border-border rounded-md mt-1" value={edit.color} onChange={(e) => setEdit({ ...edit, color: e.target.value })} /></label></div>
            <F k="image" label="Image URL" /><F k="short_description" label="Short Description" ta /><F k="description" label="Description" ta /><F k="ingredient_story" label="Ingredient Story" ta />
            <F k="ingredients" label="Ingredients" /><F k="nutrition" label="Nutrition" /><F k="pack_sizes" label="Pack Sizes (comma separated)" /><F k="verified_claims" label="Verified Claims (comma separated)" /><F k="usage" label="Usage (one per line)" ta />
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-muted-foreground">Status</span><select className="w-full border border-border rounded-md p-2 mt-1 bg-white text-sm" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="product-status-select">{PRODUCT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
              <label className="flex items-center gap-2 mt-6"><input type="checkbox" checked={!!edit.featured} onChange={(e) => setEdit({ ...edit, featured: e.target.checked })} /><span className="text-sm">Featured</span></label>
            </div>
            <button onClick={save} className="w-full bg-botanical text-ivory rounded-full py-3 font-medium" data-testid="save-product-btn">Save Product</button>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const save = async () => { try { await api.put("/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const F = ({ k, label }) => (<label className="block"><span className="text-xs text-muted-foreground">{label}</span><input className="w-full border border-border rounded-md p-2.5 mt-1 bg-white text-sm" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} /></label>);
  return (
    <div className="max-w-2xl">
      <h1 className="font-serif text-4xl text-botanical mb-8">Company Settings</h1>
      <div className="grid md:grid-cols-2 gap-4">
        <F k="name" label="Company Name" /><F k="brand" label="Brand" /><F k="email" label="Email" /><F k="phone" label="Phone" /><F k="whatsapp" label="WhatsApp" /><F k="cin" label="CIN" />
        <F k="instagram" label="Instagram URL" /><F k="facebook" label="Facebook URL" /><F k="linkedin" label="LinkedIn URL" />
      </div>
      <div className="mt-4"><label className="block"><span className="text-xs text-muted-foreground">Registered Office</span><textarea rows={2} className="w-full border border-border rounded-md p-2.5 mt-1 bg-white text-sm" value={s.address ?? ""} onChange={(e) => setS({ ...s, address: e.target.value })} /></label></div>
      <button onClick={save} className="mt-6 bg-botanical text-ivory rounded-full px-8 py-3 font-medium" data-testid="save-settings-btn">Save Settings</button>
    </div>
  );
}
